import { inject } from '@angular/core'
import { provideAppInitializer } from '@angular/core'
import { Auth } from '@angular/fire/auth'

import { onAuthStateChanged, Unsubscribe } from 'firebase/auth'
import { catchError, of, timeout } from 'rxjs'

import { AUTH_SERVICE } from '../tokens/auth-service.token'
import { AUTH_STORE } from '../tokens/auth-store.token'
import { ENVIRONMENT } from '../tokens/environment.token'

import type { User as FirebaseUser } from 'firebase/auth'
import type { Observable } from 'rxjs'

/**
 * Provides an application initializer for authentication setup.
 *
 * Authentication flow:
 * 1. First, try to restore session using stored refresh token (fast, no Firebase dependency)
 * 2. If refresh succeeds, user is authenticated immediately
 * 3. If refresh fails or no stored token, fall back to Firebase auth flow
 *
 * This ensures returning users get instant authentication without waiting for Firebase.
 *
 * @returns A function that initializes the authentication process and returns
 * a promise that resolves once the authentication state is set.
 */
export const provideAuthInitializer = () => {
  return provideAppInitializer(() => {
    const auth = inject(Auth)
    const authService = inject(AUTH_SERVICE)
    const authStore = inject(AUTH_STORE)
    const env = inject(ENVIRONMENT)

    auth.tenantId = env.tenantId

    return new Promise<void>((resolve) => {
      const hasStoredSession = authStore.hasStoredSession()

      if (hasStoredSession) {
        // Fast path: try to restore session using HttpOnly cookie
        authService
          .refreshAccessToken()
          .pipe(
            timeout(5000), // 5 second timeout for refresh call
            catchError((error) => {
              console.warn('Session refresh failed or timed out, falling back to Firebase:', error)
              return of(null)
            })
          )
          .subscribe({
            next: (accessToken) => {
              if (accessToken) {
                // Refresh succeeded - restore session from storage
                const restored = authStore.restoreSessionFromStorage()
                if (restored) {
                  console.log('Session restored from HttpOnly cookie')
                  resolve()
                  return
                }
              }
              // Refresh failed or couldn't restore - fall back to Firebase
              initializeWithFirebase(auth, authService, authStore, resolve)
            },
            error: () => {
              // Unexpected error - fall back to Firebase
              initializeWithFirebase(auth, authService, authStore, resolve)
            }
          })
      } else {
        // No stored session - use Firebase flow
        initializeWithFirebase(auth, authService, authStore, resolve)
      }
    })
  })
}

/**
 * Initialize authentication using Firebase auth state.
 * This is the fallback when no stored refresh token is available.
 */
function initializeWithFirebase(
  auth: Auth,
  authService: { loginToYoshuApi: (user: FirebaseUser) => Observable<unknown> },
  authStore: { getIsLoggingIn: () => boolean; clearAuth: () => void },
  resolve: () => void
) {
  let resolved = false
  let unsubscribe: Unsubscribe | null = null

  const resolveOnce = () => {
    if (!resolved) {
      resolved = true
      if (unsubscribe) {
        unsubscribe()
      }
      resolve()
    }
  }

  // Timeout for Firebase to respond
  const firebaseTimeoutId = setTimeout(() => {
    if (!resolved) {
      console.error('Firebase auth state timed out after 10 seconds')
      authStore.clearAuth()
      resolveOnce()
    }
  }, 10000)

  unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
    clearTimeout(firebaseTimeoutId)

    if (firebaseUser && !authStore.getIsLoggingIn()) {
      const apiTimeoutId = setTimeout(() => {
        if (!resolved) {
          console.error('Auth API call timed out after 10 seconds')
          authStore.clearAuth()
          resolveOnce()
        }
      }, 10000)

      authService
        .loginToYoshuApi(firebaseUser)
        .pipe(
          catchError((error) => {
            console.error('Failed to login to Yoshu API:', error)
            authStore.clearAuth()
            return of(null)
          })
        )
        .subscribe({
          next: () => {
            clearTimeout(apiTimeoutId)
            console.log('Successfully logged in via Firebase')
            resolveOnce()
          },
          error: (error: unknown) => {
            clearTimeout(apiTimeoutId)
            console.error('Unexpected error in Firebase auth:', error)
            authStore.clearAuth()
            resolveOnce()
          },
          complete: () => {
            clearTimeout(apiTimeoutId)
            resolveOnce()
          }
        })
    } else {
      resolveOnce()
    }
  })
}
